// Web push via Firebase Cloud Messaging.
//
// The dashboard calls registerPushToken() once the user is signed in: it asks
// for notification permission, generates this browser's FCM token and stores it
// on the backend. The token is then kept in localStorage (fcm_token) so later dashboard
// visits skip the whole round trip. Logout removes it from the backend before
// localStorage is cleared, so the next login registers again for the new user.

import { getMessaging, getToken, deleteToken, isSupported } from "firebase/messaging";
import { app } from "./firebaseApp";
import { postApi } from "./apiService";

export const FCM_TOKEN_KEY = "fcm_token";

// Firebase Console > Project settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const PUBLIC_URL = process.env.PUBLIC_URL || "";

// The dashboard can mount twice (StrictMode, quick navigation) - share one run.
let pendingRegistration = null;

function getStoredToken() {
  try {
    return localStorage.getItem(FCM_TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

function getPlatform() {
  const ua = navigator.userAgent || "";
  // iPadOS reports itself as a Mac; touch support gives it away.
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return "ios";
  }
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

async function getMessagingIfSupported() {
  if (typeof window === "undefined" || typeof Notification === "undefined") return null;
  if (!("serviceWorker" in navigator)) return null;
  if (!(await isSupported().catch(() => false))) return null;
  return getMessaging(app);
}

// Kept on its own scope so it never fights the PWA worker in service-worker.js.
// getToken() needs an *active* worker - on the first visit register() resolves
// while it is still installing, so wait for it to finish activating.
async function registerMessagingWorker() {
  const registration = await navigator.serviceWorker.register(
    `${PUBLIC_URL}/firebase-messaging-sw.js`,
    { scope: `${PUBLIC_URL}/firebase-cloud-messaging-push-scope` }
  );
  if (registration.active) return registration;

  const worker = registration.installing || registration.waiting;
  await new Promise((resolve, reject) => {
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve();
      if (worker.state === "redundant") reject(new Error("FCM service worker failed to install"));
    });
  });
  return registration;
}

async function requestPermission() {
  if (Notification.permission !== "default") return Notification.permission;
  // Older Safari only supports the callback form and returns undefined.
  const result = await new Promise((resolve) => {
    const maybePromise = Notification.requestPermission(resolve);
    if (maybePromise && typeof maybePromise.then === "function") {
      maybePromise.then(resolve).catch(() => resolve(Notification.permission));
    }
  });
  return result || Notification.permission;
}

async function generateAndRegisterToken() {
  if (!VAPID_KEY) {
    console.warn("Push notifications skipped: REACT_APP_FIREBASE_VAPID_KEY is not set");
    return null;
  }

  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  const permission = await requestPermission();
  if (permission !== "granted") return null;

  const serviceWorkerRegistration = await registerMessagingWorker();
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration });
  if (!token) return null;

  console.log("FCM token:", token);
  localStorage.setItem(FCM_TOKEN_KEY, token);

  try {
    await postApi("/register-device-token", { token, platform: getPlatform() });
  } catch (error) {
    // Not on the backend yet - drop the cached copy so the next visit retries.
    localStorage.removeItem(FCM_TOKEN_KEY);
    throw error;
  }
  return token;
}

/**
 * Generates this device's FCM token and saves it on the backend, unless that
 * was already done (token found in localStorage). Never throws.
 */
export function registerPushToken() {
  const stored = getStoredToken();
  if (stored) return Promise.resolve(stored);

  if (!pendingRegistration) {
    pendingRegistration = generateAndRegisterToken()
      .catch((error) => {
        console.error("Push token registration failed:", error);
        return null;
      })
      .finally(() => {
        pendingRegistration = null;
      });
  }
  return pendingRegistration;
}

/**
 * Logout: stop pushes to this device for the signed-out user. Call before
 * localStorage is cleared. Never throws.
 */
export async function removePushToken() {
  const token = getStoredToken();
  if (!token) return;

  try {
    await postApi("/remove-device-token", { token });
  } catch (error) {
    console.error("Push token removal failed:", error);
  }

  try {
    const messaging = await getMessagingIfSupported();
    if (messaging) await deleteToken(messaging);
  } catch (_) {
    /* the backend no longer has it, which is what matters */
  }

  localStorage.removeItem(FCM_TOKEN_KEY);
}
